/**
 * Milano Wigs — PDP Personalization Panel v2
 * Palo Alto 9.1 + SC Options App Embed
 * v2026-05-11
 *
 * CHANGE (v2026-05-11): FORM ATTRIBUTE STRINGIFICATION FIX
 * - FIX: getProductForm() returns a Shopify product <form> that contains
 *   <input name="id" value="{{ variant.id }}">. Per HTML spec, named form
 *   children become properties of the form element, so `form.id` returns
 *   the hidden variant-id INPUT, not the form's id attribute string.
 *   Stringifying that input to setAttribute('form', ...) produced
 *   form="[object HTMLInputElement]", which:
 *     1. Detached relocated SC Options inputs from form submission
 *        (attribute pointed at a non-existent form id)
 *     2. Triggered downstream pixel validation errors (Klaviyo, Angler)
 *        when those scripts tried to read root.form.id as a primitive.
 * - Fix: read the id via getAttribute('id'), which always returns the
 *   attribute string and bypasses the named-element collision.
 *
 * CHANGE (v2026-05-08): MOTHER'S DAY SALE ENHANCEMENTS
 * - ADDED: injectSaleBanner() — tasteful PDP sale indicator below price.
 *   Reads mw_dn_ratio from sessionStorage, renders dynamic % badge +
 *   warm promotional copy. Hidden in wizard mode. Self-removing when
 *   ratio clears. POST-SALE: remove .mw-sale-banner CSS block.
 *
 * CHANGE (v2026-05-07b): PDP PRICE + CART DRAWER PERSISTENCE + RATIO FIX
 * - ADDED: syncProductPrice() — renders strikethrough on PDP product price
 *   when mw_dn_ratio exists in sessionStorage. Sibling .mw-pdp-strike
 *   element, hides original via class. CSS order:-1 positions sale price
 *   before wizard's (+$X services) in flex layouts.
 * - ADDED: storeDNRatio() — stores DN discount ratio in sessionStorage.
 *   Guard: only touches sessionStorage when DN DOM elements are present,
 *   preserving ratio set from collection pages when DN is off on PDP.
 * - FIX: computeFinalPrice() uses stored ratio for accurate percentage
 *   discount on SC Options total including add-ons.
 * - FIX: Cart drawer strikethrough persists across re-renders. Added
 *   theme:cart:change listener and drawer-open body class observer.
 *
 * CHANGE (v2026-05-07a): DN CART DRAWER PRICE CORRUPTION FIX
 * - REMOVED: updateDNDisplay(), updateDNDataAttrs(), findDNStrikeTextEl(),
 *   getDNOriginalPriceCents(), getDNDiscountRatio(), resetDNVariantBase(),
 *   weAreWritingDN flag, _dnVariantBaseCents, _dnDiscountRatio variables.
 * - These functions wrote SC Options add-on-inflated prices into DN's DOM
 *   elements, causing a MutationObserver write-war loop. DN then mapped
 *   wrong prices to cart drawer rows, displaying wig prices on accessories.
 * - computeFinalPrice() simplified: uses flat DN discount subtraction
 *   instead of ratio-based math. Cart bar still shows correct final price.
 * - ATC button price HIDDEN via injected CSS — eliminates the DN vs SC
 *   Options price conflict on the button entirely. Price shown on product
 *   form (DN manages), cart bar (our sync), wizard review card, and cart
 *   drawer (native Liquid). findATCTargets() and ATC writing removed from
 *   doSyncFast() — no DOM writes to fight over.
 * - DN MutationObserver simplified: removed weAreWritingDN guard and
 *   resetDNVariantBase() call. Observer now only invalidates cache and
 *   triggers cooldown on DN re-renders.
 * - Paired with cart-line-items.liquid change: DN per-item hooks
 *   (data-la-dn-cart-item-row/key/line-price/text) removed. Cart-level
 *   DN hooks in cart-drawer.liquid unchanged.
 *
 * CHANGE (v2026-04-01a): REMOVED BOOST QUICK VIEW CODE
 * - Deleted Boost SD V3 quick view IIFE (former lines 2001-2328).
 *   Globo SPF replaces Boost for collection page filtering/search.
 *   Quick view PDP redirect for wigs/toppers ($150+) now handled
 *   by a lightweight click listener in theme.liquid.
 * - Cart Drawer strikethrough IIFE retained (runs on all pages).
 *
 * FIX (v2026-03-27a): SHOP THIS LOOK / SIMPLIFIED OPTION SET SUPPORT
 * - Option sets without a "Personalize It" toggle (e.g., "Shop This Look -
 *   Divine", "Shop This Look - Silk") had their non-cap-size options
 *   (Secure Method, Add Ear Pieces) classified as personalization options
 *   and placed into the accordion panel. Without a toggle, the panel stays
 *   display:none forever — options silently disappear from the PDP.
 * - Added defensive fallback in buildPanel(): if no "Personalize It" SC
 *   option exists AND no .personalize-toggle Liquid snippet is on the page,
 *   ALL remaining options are promoted to always-visible so they render
 *   directly on the PDP. Products with a toggle (e.g., full Divine/Ponytail
 *   sets with 10 options) keep their accordion behavior unchanged.
 *
 * FIX (v2026-03-16a): Quick view enhancement uses price threshold ($150).
 *   Wigs/toppers ($200+) get full enhancement.
 *   Accessories (under $150) keep default Boost quick view.
 *
 * FIX (v2025-03-10b): DOUBLE-INIT GUARD + DEAD CODE CLEANUP
 * - syncStarted was declared inside syncATCPrice() as a local variable,
 *   making the double-call guard ineffective. syncATCPrice() is called
 *   twice on every PDP load (setTimeout 3500ms + waitForBoldOptions path),
 *   producing two concurrent RAF loops and two sets of MutationObservers.
 *   Fix: hoisted _syncStarted to module scope; reset in shopify:section:load
 *   so sync restarts correctly after theme editor section reloads.
 * - Removed unused local variable lastComputedDisplay (declared, never read).
 * - Fixed indentation on theme:cart:added listener (cosmetic).
 *
 * FIX (v2025-03-10a): ATC SAFETY NET + PERF + OVERFLOW GUARD
 * - PERF: Wraps heavy PDP-only init (polling, RAF loop, MutationObservers)
 *   in isPDP guard. Non-PDP pages skip 20s of polling + perpetual RAF.
 *   Quick View and Cart Drawer features still run globally.
 * - ATC SAFETY NET: SC Options hides the original ATC button (bold_hidden,
 *   display:none) and shows a bold_clone copy. If the clone's SC Options
 *   event handler hasn't wired up yet (slow mobile JS), tapping ATC does
 *   nothing. Safety net: a delegated click listener on .bold_clone buttons
 *   sets a 3-second timeout. If theme:cart:added doesn't fire in that
 *   window, it forces the form submit as a fallback.
 * - OVERFLOW GUARD: showSecurePopup sets body overflow:hidden which can
 *   get stuck on mobile if backdrop close fails. Added 15-second auto-
 *   clear failsafe that restores overflow if popup is still open.
 *
 * HOTFIX (v2025-03-06a-hotfix): CART DRAWER AJAX STRIKETHROUGH
 * - Palo Alto wraps drawer in <template>; content not in live DOM on load.
 *   MutationObserver approach failed (nothing to observe at init time).
 * - Fix: Listen for Palo Alto's "theme:cart:added" event (fires after
 *   build() renders items into live DOM), fetch /cart.json, rewrite
 *   .cart__item__price with <ins>/<del> where original_price > final_price.
 * - Also removed data-la-dn-cart-item-row from cart-line-items.liquid to
 *   prevent DN from overwriting per-item prices with base-variant-only values.
 * - Existing Custom CSS (.cart__item__price ins/del) styles the output.
 *
 * FIX (v2025-03-06a): PERCENTAGE DISCOUNT RATIO FOR SC OPTIONS
 * - (Superseded by v2026-05-07a — ratio math removed, flat subtraction used)
 *
 * FIX (v2025-02-27c): DN DISCOUNT DETECTION — CONFIRMED APPROACH
 * - DN's la:dn:product:discount:calculated event does NOT fire in Dynamic
 *   Pricing mode (confirmed via live testing 2026-03-03).
 * - getDNDiscountCents() parses DN's rendered DOM elements using DN-specific
 *   classes confirmed present in live DOM (.limoniapps-discountninja-
 *   strikethrough, -shortcode-discounted_price). Zero false positives —
 *   Palo Alto's native compare-at uses <s data-compare-price>.
 * - Tries data attributes first (exact cents), falls back to text parsing.
 * - Cached 800ms to avoid redundant DOM reads per sync cycle.
 * - Removed unused API event listener (no IIFE-scoped variables needed).
 *
 * FIX (v2025-02-27b): DN DYNAMIC PRICING INTEGRATION
 * - DN switched from "Embedded Tags" mode (CSS classes) to "Dynamic Pricing"
 *   mode (data-la-dn-* data attributes). Updated DN observer to watch
 *   [data-la-dn-price] container (fallback to old class).
 * - Quick View: adds data-la-dn-price on price element for DN targeting.
 * - DN element finders (getDNDiscountCents, findDNSaleTextEl)
 *   use ONLY DN-specific classes/attributes — never generic <s>/<del> parsing,
 *   which would false-positive match Palo Alto's native compare-at price markup.
 *
 * FIX (v2025-02-27a): DN DYNAMIC PRICING COMPATIBILITY
 * - ATC + cart bar sync is EVENT-DRIVEN when no add-ons are selected
 *   (scTotalCents=0) — DN owns ATC entirely, no flicker.
 * - When SC Options IS active (add-ons selected), RAF loop enforces
 *   our computed price at 200ms because DN Dynamic Pricing continuously
 *   overwrites ATC with base discounted price (ignoring add-on costs).
 *   Checks actual DOM text, not cached value, to detect DN overwrites.
 * - When SC Options is not active (scTotalCents=0), ATC is never touched —
 *   DN owns the ATC button entirely for strikethrough pricing.
 * - RAF loop also handles BNPL monitoring + Splitit recovery (500ms).
 * - No initial doSyncFast() on startup — DN renders first.
 * - Quick view: uses DN's official API (data-la-dn-product-handle +
 *   data-la-dn-product-quickview + la:dn:collection:updated event).
 *
 * FIX (v2025-02-25b): DN + SC OPTIONS PRICE UNIFICATION
 * - DN hides original [data-product-price] inside display:none wrapper.
 *   SC Options updates the hidden element; visible DN price never reflects
 *   add-on costs. ATC/cart-bar/BNPL all sync to wrong (un-discounted) total.
 * - New approach: computeFinalPrice() reads SC Options total (variant+addons)
 *   from the hidden element, subtracts DN discount (read from DN data attrs),
 *   produces the true customer-facing price.
 * - All downstream sync (ATC buttons, cart bar, Splitit, Klarna) uses the
 *   computed final price.
 * - DN discount auto-updates on variant change (DN re-renders, RAF re-reads).
 *
 * FIX (v2025-02-25a): DISCOUNT NINJA PDP SUPPORT
 * - findSource: When SC Options is not present, checks for DN's discounted
 *   price element before falling back to regular Palo Alto price.
 *   SC Options still takes priority when present.
 *
 * FIX (v2025-02-16c): BNPL SYNC REWORK + ATC CLONE + UNIVERSAL SYNC
 * - Splitit: Use official API splitit.ui.refresh() per SDK docs. Cache HTML,
 *   restore when Palo Alto section rendering destroys container.
 * - Klarna: Attribute-only update (no refresh-placements). RAF loop keeps
 *   overriding when Klarna's app block reverts on variant change.
 * - ATC price: Target ALL [data-product-price] in ATC area, not just first.
 *   SC Options bold_clone button now gets synced.
 * - syncATCPrice always starts (even without SC Options) for BNPL sync.
 * - findSource falls back to regular price element without SC Options.
 *
 * FIX (v2025-02-16a): FORM CONTAINMENT
 * - .personalize-toggle renders in Palo Alto's "title" block, OUTSIDE the
 *   product <form>. buildPanel() used toggle as DOM anchor → moved all
 *   SC Options inputs outside the form → properties[] never submitted.
 * - Fix: relocate toggle inside the form before using it as anchor.
 * - Safety net: stamp form="<formId>" on all moved inputs.
 *
 * Prior changes (v2025-02-11a):
 * - Hides .bold_option_title inside accordion rows (prevents double title)
 * - Re-extracts option name when conditional options become visible
 * - Hides empty rows (conditional options with no visible form elements)
 * - Moves always-visible options ABOVE the personalize toggle
 * - Styles always-visible options to match Shopify variant selectors
 */

// =============================================================================
// EARLY-BOOT NOTE (v2026-05-08): The sale price strikethrough renders ~2-3s
// after page load (when syncATCPrice starts). Colors match DN's collection page
// rendering (#C45349 sale / #9A8E84 strike) so the transition is visually smooth
// even if there's a brief flash. For a persistent sale (>2 weeks), consider
// moving syncProductPrice into a DOMContentLoaded handler for faster rendering.
// =============================================================================

(function() {
  'use strict';

  if (window.MilanoPersonalizeInit) return;
  window.MilanoPersonalizeInit = true;

  var VERSION = 'v2026-05-11';
  console.log('[Milano] personalize-options.js ' + VERSION + ' loaded');

  // =========================================================================
  // PAGE DETECTION — Skip heavy PDP work on non-product pages
  //
  // This script loads sitewide (needed for Quick View on collections and
  // Cart Drawer strikethrough on all pages). But the heavy initialization
  // — 20s of SC Options polling, RAF price sync loop, MutationObservers —
  // should only run on PDP.
  // =========================================================================

  var isPDP = window.location.pathname.indexOf('/products/') !== -1;
  if (!isPDP) {
    console.log('[Milano] Non-PDP page — skipping panel/sync init');
  }

  // =========================================================================
  // STATE
  // =========================================================================

  var state = {
    container: null,
    panelBuilt: false,
    building: false,
    rows: [],
    currentlyOpen: null,
    panelEl: null,
    contentEl: null,
    summaryEl: null,
    observers: []
  };

  // Module-level guard: prevents syncATCPrice() from initializing twice.
  // Must live here (not inside syncATCPrice) so repeated calls are blocked
  // across executions. Reset by shopify:section:load for theme-editor reloads.
  var _syncStarted = false;

  // =========================================================================
  // CONFIGURATION
  // =========================================================================

  var ALWAYS_VISIBLE_EXACT = [
    'adjust cap size',
    'adjust ponytail cap size',
    'adjust princess cap size',
    'topper base size',
    'fringe topper base size',
    'new cap size',
    'select cap size'
  ];

  var ALWAYS_VISIBLE_KEYWORDS = [
    'fedora', 'baseball caps', 'cap color',
    'lace front options', 'deep lace'
  ];

  // =========================================================================
  // HELP LINKS — "What's This?" popup mapping
  // Maps SC Options names → secure-popup container IDs.
  // Order matters: first match wins. Put specific patterns before general.
  // =========================================================================

  var HELP_LINKS = [
    // Secure methods — product-specific variants (check these first)
    //{ pattern: /wavy.*secure|secure.*wavy/i, popup: 'wavy-secure-method' },
    { pattern: /onyx.*secure|secure.*onyx/i, popup: 'onyx-secure-method' },
    { pattern: /diamond.*secure|secure.*diamond/i, popup: 'diamond-secure-method' },
    { pattern: /topaz.*secure|emerald.*secure|secure.*topaz|secure.*emerald/i, popup: 'topeme-secure-method' },
    { pattern: /topper.*secure|secure.*topper/i, popup: 'dtoppers-secure-method' },
    // Secure method — generic fallback (Divine, or any unmatched)
    { pattern: /secure method/i, popup: 'secure-method' },

    // Cap size / base size
    { pattern: /base size/i, popup: 'base-size-topaz-emerald' },
    { pattern: /cap size/i, popup: 'new-cap-size' },

    // Lace / front hairline
    { pattern: /lace front|deep lace/i, popup: 'lace-compare' },
    { pattern: /front hairline|blending.*hairline/i, popup: 'front-hairline' },

    // Color
    { pattern: /color service/i, popup: 'color-service' },

    // Cut, style, part
    { pattern: /personalize your cut/i, popup: 'personalize-your-cut-wigs-toppers' },
    { pattern: /signature cut/i, popup: 'milano-signature' },
    { pattern: /style my.*wavy|wavy.*style/i, popup: 'please-style-my-wig-wavy' },
    { pattern: /style my wig|please style/i, popup: 'please-style-my-wig' },
    { pattern: /location of part/i, popup: 'location-of-part' },
    { pattern: /face fram/i, popup: 'face-farming-position' },

    // Ear pieces
    { pattern: /ear piece/i, popup: 'ear-pieces' }
  ];

  function findHelpPopupId(optionName) {
    if (!optionName) return null;
    for (var i = 0; i < HELP_LINKS.length; i++) {
      if (HELP_LINKS[i].pattern.test(optionName)) {
        // Verify the popup container exists on this page
        var el = document.getElementById(HELP_LINKS[i].popup);
        if (el) return HELP_LINKS[i].popup;
      }
    }
    return null;
  }

  function createHelpLink(popupId) {
    var link = document.createElement('a');
    link.href = '#' + popupId;
    link.className = 'milano-help-link';
    link.textContent = 'What\'s This?';
    link.setAttribute('role', 'button');
    link.setAttribute('aria-label', 'Learn more');
    link.addEventListener('click', function(e) {
      e.preventDefault();
      e.stopPropagation(); // Don't toggle accordion row
      showSecurePopup(popupId);
    });
    return link;
  }

  // =========================================================================
  // SECURE POPUP — Show / Hide
  // =========================================================================

  function showSecurePopup(id) {
    var container = document.getElementById(id);
    if (!container || !container.classList.contains('pop-up-container')) {
      // Fallback querySelector with safe escaping
      try {
        var escaped = (CSS && CSS.escape) ? CSS.escape(id) : id.replace(/[^a-zA-Z0-9_-]/g, '\\$&');
        container = document.querySelector('.pop-up-container#' + escaped);
      } catch (e) { /* ignore */ }
    }
    if (!container) {
      console.warn('[Milano] Popup not found: #' + id);
      return;
    }
    container.style.display = 'flex';
    document.body.style.overflow = 'hidden';
    console.log('[Milano] Popup opened: #' + id);

    // Safety: auto-clear overflow if popup stays open >15s (mobile close can fail)
    setTimeout(function() {
      if (document.body.style.overflow === 'hidden') {
        var stillOpen = document.querySelector('.pop-up-container[style*="flex"]');
        if (stillOpen) {
          hideAllSecurePopups();
          console.log('[Milano] Popup overflow safety — auto-closed after 15s');
        }
      }
    }, 15000);
  }

  function hideAllSecurePopups() {
    var open = document.querySelectorAll('.pop-up-container');
    var closed = false;
    for (var i = 0; i < open.length; i++) {
      if (open[i].style.display === 'flex' || getComputedStyle(open[i]).display === 'flex') {
        open[i].style.display = 'none';
        closed = true;
      }
    }
    if (closed) {
      document.body.style.overflow = '';
      console.log('[Milano] Popups closed');
    }
  }

  // Global close handlers (run once)
  if (!window._milanoPopupHandlers) {
    window._milanoPopupHandlers = true;

    // Close button
    document.addEventListener('click', function(e) {
      if (e.target.classList.contains('close-button-img') ||
          e.target.closest('.close-button')) {
        hideAllSecurePopups();
      }
    });

    // Backdrop click (click on .pop-up-container itself, not its children)
    document.addEventListener('click', function(e) {
      if (e.target.classList.contains('pop-up-container')) {
        hideAllSecurePopups();
      }
    });

    // ESC key
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' || e.keyCode === 27) {
        hideAllSecurePopups();
      }
    });
  }

  // =========================================================================
  // DOM: Find the correct SC Options container
  // =========================================================================

  function findOptionsContainer() {
    var loaded = document.querySelector('.bold_options.bold_options_loaded');
    if (loaded) return loaded;

    var all = document.querySelectorAll('.bold_options');
    for (var i = 0; i < all.length; i++) {
      if (all[i].querySelector('.bold_option')) return all[i];
    }
    return null;
  }

  // =========================================================================
  // DOM: Find the product <form>
  // =========================================================================

  function getProductForm() {
    return document.querySelector('form[action*="/cart/add"]')
        || document.querySelector('.product__form')
        || document.querySelector('[data-product-form]');
  }

  // =========================================================================
  // DOM: Stamp form="" attribute on all inputs inside a container
  // =========================================================================

  function stampFormAttribute(el, formId) {
    if (!el || !formId) return;
    var inputs = el.querySelectorAll('input, select, textarea');
    for (var i = 0; i < inputs.length; i++) {
      if (!inputs[i].hasAttribute('form')) {
        inputs[i].setAttribute('form', formId);
      }
    }
  }

  // =========================================================================
  // DOM: Extract option name from .bold_option element
  // =========================================================================

  function getOptionName(el) {
    // 1. data-option-name attribute
    var name = el.getAttribute('data-option-name');
    if (name && name.trim()) return name.trim();

    // 2. properties[...] from input name attributes
    var inputs = el.querySelectorAll('select, input, textarea');
    for (var i = 0; i < inputs.length; i++) {
      var n = inputs[i].getAttribute('name') || '';
      var m = n.match(/properties\[([^\]]+)\]/);
      if (m) return m[1].trim();
    }

    // 3. .bold_option_title text
    var title = el.querySelector('.bold_option_title');
    if (title) {
      var text = (title.textContent || '').trim()
        .replace(/\*$/, '').replace(/:$/, '').trim();
      if (text) return text;
    }

    // 4. First label text
    var label = el.querySelector('label');
    if (label) {
      var lt = (label.textContent || '').trim();
      if (lt && lt.length < 80) return lt;
    }

    // 5. Heading
    var heading = el.querySelector('h1,h2,h3,h4,h5,h6,strong,b,.bold_option_header');
    if (heading) return (heading.textContent || '').trim();

    return '';
  }

  // =========================================================================
  // Check if an option element has actual form content
  // =========================================================================

  function hasFormContent(el) {
    var selects = el.querySelectorAll('select');
    var inputs = el.querySelectorAll('input[type="radio"], input[type="checkbox"]');
    var textInputs = el.querySelectorAll('input[type="text"], textarea');
    var title = el.querySelector('.bold_option_title');
    return (selects.length > 0 || inputs.length > 0 || textInputs.length > 0 || (title && title.textContent.trim()));
  }

  // =========================================================================
  // CLASSIFICATION
  // =========================================================================

  function isAlwaysVisible(name) {
    var lower = name.toLowerCase();
    for (var a = 0; a < ALWAYS_VISIBLE_EXACT.length; a++) {
      if (lower === ALWAYS_VISIBLE_EXACT[a]) return true;
    }
    for (var k = 0; k < ALWAYS_VISIBLE_KEYWORDS.length; k++) {
      if (lower.indexOf(ALWAYS_VISIBLE_KEYWORDS[k]) > -1) return true;
    }
    return false;
  }

  function isPersonalizeIt(name) {
    return name.toLowerCase() === 'personalize it';
  }

  // =========================================================================
  // TOGGLE STATE
  // =========================================================================

  function getPersonalizeState() {
    try {
      var checkbox = document.querySelector('[data-personalize-input][type="checkbox"]');
      if (checkbox) return checkbox.checked;
      var radio = document.querySelector('[data-personalize-input]:checked');
      if (radio) return radio.value === 'Yes';
      var sel = document.querySelector('select[name="properties[Personalize It]"]');
      if (sel) return sel.value === 'Yes';
    } catch (e) {}
    return false;
  }

  function updatePersonalizeState() {
    var isYes = getPersonalizeState();
    document.body.classList.toggle('personalize-yes', isYes);

    if (state.contentEl) {
      if (isYes) {
        state.contentEl.style.display = '';
        state.contentEl.style.maxHeight = '0px';
        void state.contentEl.offsetHeight;
        state.contentEl.style.transition = 'max-height 0.4s ease, opacity 0.3s ease';
        state.contentEl.style.opacity = '1';
        state.contentEl.style.maxHeight = state.contentEl.scrollHeight + 'px';
        setTimeout(function() {
          state.contentEl.style.maxHeight = 'none';
          state.contentEl.style.transition = '';
        }, 450);
      } else {
        collapseAllRows();
        state.contentEl.style.transition = 'max-height 0.3s ease, opacity 0.2s ease';
        state.contentEl.style.maxHeight = state.contentEl.scrollHeight + 'px';
        void state.contentEl.offsetHeight;
        state.contentEl.style.opacity = '0';
        state.contentEl.style.maxHeight = '0px';
        setTimeout(function() {
          state.contentEl.style.display = 'none';
          state.contentEl.style.transition = '';
          state.contentEl.style.opacity = '';
        }, 350);
      }
    }

    var hidden = document.querySelector('[data-personalize-hidden]');
    if (hidden) hidden.value = isYes ? 'Yes' : 'As Is';

    updateSummary();
  }

  // =========================================================================
  // SELECTION PREVIEW
  // =========================================================================

  function getSelectionPreview(boldOption) {
    var select = boldOption.querySelector('select');
    if (select && select.selectedIndex >= 0) {
      var text = select.options[select.selectedIndex].text.trim();
      if (text.indexOf('...') > -1 || text.indexOf('Select') > -1 || text === '') {
        return { text: '', hasSelection: false };
      }
      return { text: truncate(stripPrice(text), 28), hasSelection: true };
    }

    var checkedRadio = boldOption.querySelector('input[type="radio"]:checked');
    if (checkedRadio) {
      var rLabel = findLabelFor(boldOption, checkedRadio);
      return { text: truncate(stripPrice(rLabel), 28), hasSelection: true };
    }

    var checked = boldOption.querySelectorAll('input[type="checkbox"]:checked');
    if (checked.length > 0) {
      if (checked.length === 1) {
        var cLabel = findLabelFor(boldOption, checked[0]);
        return { text: truncate(stripPrice(cLabel), 28), hasSelection: true };
      }
      return { text: checked.length + ' selected', hasSelection: true };
    }

    return { text: '', hasSelection: false };
  }

  function findLabelFor(container, input) {
    if (input.id) {
      var label = container.querySelector('label[for="' + input.id + '"]');
      if (label) return label.textContent.trim();
    }
    var parent = input.closest('label');
    if (parent) return parent.textContent.trim();
    var next = input.nextElementSibling;
    if (next && next.tagName === 'LABEL') return next.textContent.trim();
    return input.value || '';
  }

  function stripPrice(text) {
    return text
      .replace(/\s*\(?\+?\$[\d,]+\.?\d{0,2}\s*USD\)?/gi, '')
      .replace(/\s*\$[\d,]+\.?\d{0,2}\s*USD/gi, '')
      .trim();
  }

  function truncate(str, max) {
    if (str.length <= max) return str;
    return str.substring(0, max - 1) + '\u2026';
  }

  // =========================================================================
  // PRICE EXTRACTION
  // =========================================================================

  function extractPrice(text) {
    var match = text.match(/\+?\$?([\d,]+\.?\d{0,2})\s*USD/i);
    if (match) return parseFloat(match[1].replace(/,/g, ''));
    match = text.match(/\$(\d+(?:\.\d{2})?)/);
    if (match) return parseFloat(match[1]);
    return 0;
  }

  function getOptionPrice(boldOption) {
    var total = 0;

    var select = boldOption.querySelector('select');
    if (select && select.selectedIndex >= 0) {
      total += extractPrice(select.options[select.selectedIndex].text);
      return total;
    }

    var checkedRadio = boldOption.querySelector('input[type="radio"]:checked');
    if (checkedRadio) {
      var rLabel = findLabelFor(boldOption, checkedRadio);
      total += extractPrice(rLabel);
      return total;
    }

    var checked = boldOption.querySelectorAll('input[type="checkbox"]:checked');
    checked.forEach(function(cb) {
      var wrapper = cb.closest('.bold_option_value');
      var priceEl = wrapper ? wrapper.querySelector('.bold_option_value_price') : null;
      if (priceEl) {
        total += extractPrice(priceEl.textContent);
      } else {
        var cLabel = findLabelFor(boldOption, cb);
        total += extractPrice(cLabel);
      }
    });

    return total;
  }

  // =========================================================================
  // OPTION ROW
  // =========================================================================

  function createOptionRow(boldOption, name) {
    var row = document.createElement('div');
    row.className = 'milano-option-row';
    row.setAttribute('data-option-name', name);

    // Header
    var header = document.createElement('button');
    header.type = 'button';
    header.className = 'milano-option-row__header';
    header.setAttribute('aria-expanded', 'false');

    var nameSpan = document.createElement('span');
    nameSpan.className = 'milano-option-row__name';
    nameSpan.textContent = name;

    var previewSpan = document.createElement('span');
    previewSpan.className = 'milano-option-row__preview';

    var chevron = document.createElement('span');
    chevron.className = 'milano-option-row__chevron';
    chevron.setAttribute('aria-hidden', 'true');
    chevron.innerHTML =
      '<svg width="14" height="14" viewBox="0 0 14 14" fill="none">' +
        '<path d="M3.5 5.25L7 8.75L10.5 5.25" stroke="currentColor" stroke-width="1.5" ' +
        'stroke-linecap="round" stroke-linejoin="round"/>' +
      '</svg>';

    header.appendChild(nameSpan);

    // Help link — "What's This?"
    var helpPopupId = findHelpPopupId(name);
    if (helpPopupId) {
      header.appendChild(createHelpLink(helpPopupId));
    }

    header.appendChild(previewSpan);
    header.appendChild(chevron);

    // Body
    var body = document.createElement('div');
    body.className = 'milano-option-row__body';

    boldOption.parentNode.insertBefore(row, boldOption);
    body.appendChild(boldOption);
    row.appendChild(header);
    row.appendChild(body);

    var rowData = {
      row: row,
      header: header,
      body: body,
      nameSpan: nameSpan,
      previewSpan: previewSpan,
      boldOption: boldOption,
      name: name,
      isOpen: false,
      isVisible: true
    };

    // Initial visibility check
    var computedDisplay = window.getComputedStyle(boldOption).display;
    var inlineDisplay = boldOption.style.display;
    var isHidden = inlineDisplay === 'none' || computedDisplay === 'none';
    var isEmpty = !hasFormContent(boldOption);

    if (isHidden || isEmpty) {
      row.classList.add('milano-option-row--hidden');
      rowData.isVisible = false;
    }

    updateRowPreview(rowData);

    // Click handler
    header.addEventListener('click', function(e) {
      e.preventDefault();
      if (rowData.isOpen) {
        collapseRow(rowData);
      } else {
        if (state.currentlyOpen && state.currentlyOpen !== rowData) {
          collapseRow(state.currentlyOpen);
        }
        expandRow(rowData);
      }
    });

    // Watch for input changes
    body.addEventListener('change', function() {
      setTimeout(function() {
        updateRowPreview(rowData);
        updateSummary();
        if (rowData.isOpen) {
          body.style.maxHeight = 'none';
        }
      }, 50);
    });

    return rowData;
  }

  function updateRowPreview(rowData) {
    var preview = getSelectionPreview(rowData.boldOption);
    rowData.previewSpan.textContent = preview.text;
    rowData.row.classList.toggle('milano-option-row--selected', preview.hasSelection);
  }

  function expandRow(rowData) {
    rowData.isOpen = true;
    state.currentlyOpen = rowData;
    rowData.header.setAttribute('aria-expanded', 'true');
    rowData.row.classList.add('milano-option-row--open');

    var body = rowData.body;
    body.style.display = '';
    body.style.maxHeight = '0px';
    void body.offsetHeight;
    body.style.transition = 'max-height 0.35s ease';
    body.style.maxHeight = body.scrollHeight + 'px';
    setTimeout(function() {
      body.style.maxHeight = 'none';
      body.style.overflow = 'visible';
      body.style.transition = '';
    }, 380);
  }

  function collapseRow(rowData) {
    rowData.isOpen = false;
    if (state.currentlyOpen === rowData) state.currentlyOpen = null;
    rowData.header.setAttribute('aria-expanded', 'false');
    rowData.row.classList.remove('milano-option-row--open');

    var body = rowData.body;
    body.style.overflow = 'hidden';
    body.style.transition = '';
    body.style.maxHeight = body.scrollHeight + 'px';
    void body.offsetHeight;
    body.style.transition = 'max-height 0.3s ease';
    body.style.maxHeight = '0px';
    setTimeout(function() {
      body.style.transition = '';
    }, 350);
  }

  function collapseAllRows() {
    state.rows.forEach(function(rowData) {
      if (rowData.isOpen) collapseRow(rowData);
    });
  }

  // =========================================================================
  // SC OPTIONS CONDITION WATCHER
  // =========================================================================

  function watchOptionVisibility(rowData) {
    var boldOption = rowData.boldOption;
    var debounceTimer = null;

    var observer = new MutationObserver(function() {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(function() {
        try {
          if (!rowData.row || !rowData.boldOption) return;

          var nowHidden = boldOption.style.display === 'none';
          var nowEmpty = !hasFormContent(boldOption);
          var wasVisible = rowData.isVisible;

          if ((nowHidden || nowEmpty) && wasVisible) {
            rowData.isVisible = false;
            if (rowData.row) rowData.row.classList.add('milano-option-row--hidden');
            if (rowData.isOpen) collapseRow(rowData);
            updateSummary();
          } else if (!nowHidden && !nowEmpty && !wasVisible) {
            if (rowData.name.indexOf('Option ') === 0) {
              var newName = getOptionName(boldOption);
              if (newName && newName.indexOf('Option ') !== 0) {
                console.log('[Milano] Renamed "' + rowData.name + '" → "' + newName + '"');
                rowData.name = newName;
                if (rowData.nameSpan) rowData.nameSpan.textContent = newName;
                if (rowData.row) rowData.row.setAttribute('data-option-name', newName);
              }
            }
            rowData.isVisible = true;
            if (rowData.row) {
              rowData.row.classList.remove('milano-option-row--hidden');
              rowData.row.classList.add('milano-option-row--entering');
              setTimeout(function() {
                if (rowData.row) rowData.row.classList.remove('milano-option-row--entering');
              }, 400);
            }
            updateSummary();
          }
        } catch (e) {
          console.log('[Milano] Observer error:', e.message);
        }
      }, 80);
    });

    observer.observe(boldOption, {
      attributes: true,
      attributeFilter: ['style'],
      childList: true,
      subtree: false
    });

    state.observers.push(observer);
  }

  // =========================================================================
  // SUMMARY
  // =========================================================================

  function updateSummary() {
    if (!state.summaryEl) return;

    var items = [];
    var total = 0;

    state.rows.forEach(function(rowData) {
      if (!rowData.isVisible) return;

      var preview = getSelectionPreview(rowData.boldOption);
      if (!preview.hasSelection) return;

      var price = getOptionPrice(rowData.boldOption);
      total += price;

      var checked = rowData.boldOption.querySelectorAll('input[type="checkbox"]:checked');
      if (checked.length > 1) {
        checked.forEach(function(cb) {
          var label = stripPrice(findLabelFor(rowData.boldOption, cb));
          var wrapper = cb.closest('.bold_option_value');
          var priceEl = wrapper ? wrapper.querySelector('.bold_option_value_price') : null;
          var p = priceEl ? extractPrice(priceEl.textContent) : extractPrice(findLabelFor(rowData.boldOption, cb));
          if (label && label.toLowerCase() !== 'no color service') {
            items.push({ name: label, price: p });
          }
        });
      } else {
        items.push({ name: rowData.name, value: preview.text, price: price });
      }
    });

    if (items.length === 0) {
      state.summaryEl.classList.remove('milano-summary--visible');
      return;
    }

    state.summaryEl.classList.add('milano-summary--visible');

    var html = '<div class="milano-summary__label">Your Selections</div>';
    html += '<div class="milano-summary__items">';

    items.forEach(function(item) {
      html += '<div class="milano-summary__item">';
      html += '<span class="milano-summary__item-name">';
      if (item.value) {
        html += escapeHtml(item.name) + ': <strong>' + escapeHtml(item.value) + '</strong>';
      } else {
        html += escapeHtml(item.name);
      }
      html += '</span>';
      if (item.price > 0) {
        html += '<span class="milano-summary__item-price">+$' + item.price.toFixed(2) + '</span>';
      }
      html += '</div>';
    });

    html += '</div>';

    if (total > 0) {
      html += '<div class="milano-summary__total">';
      html += '<span>Added services</span>';
      html += '<span>+$' + total.toFixed(2) + '</span>';
      html += '</div>';
    }

    state.summaryEl.innerHTML = html;
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  // =========================================================================
  // PANEL BUILDER
  // =========================================================================

  function buildPanel() {
    if (state.panelBuilt || state.building) return;

    var container = state.container || findOptionsContainer();
    if (!container) {
      console.log('[Milano] No options container found');
      return;
    }

    var allOptions = container.querySelectorAll('.bold_option');
    if (allOptions.length === 0) {
      console.log('[Milano] No .bold_option elements found');
      return;
    }

    state.building = true;
    console.log('[Milano] Building panel with ' + allOptions.length + ' options...');

    // Classify
    var alwaysVisible = [];
    var personalizeIt = [];
    var personalizationOptions = [];

    allOptions.forEach(function(opt) {
      var name = getOptionName(opt);
      if (isPersonalizeIt(name)) {
        personalizeIt.push(opt);
      } else if (isAlwaysVisible(name)) {
        alwaysVisible.push(opt);
      } else {
        personalizationOptions.push({ el: opt, name: name });
      }
    });

    console.log('[Milano] Always visible: ' + alwaysVisible.length +
      ', Personalize It: ' + personalizeIt.length +
      ', Personalization: ' + personalizationOptions.length);

    // -----------------------------------------------------------------------
    // DEFENSIVE FALLBACK: "Shop This Look" and other simplified option sets
    // -----------------------------------------------------------------------
    var hasToggle = !!document.querySelector('.personalize-toggle');
    if (personalizeIt.length === 0 && !hasToggle && personalizationOptions.length > 0) {
      console.log('[Milano] No "Personalize It" toggle found (no SC option, no snippet) — promoting ' +
        personalizationOptions.length + ' option(s) to always-visible');
      document.body.classList.add('milano-shop-this-look');
      personalizationOptions.forEach(function(item) {
        alwaysVisible.push(item.el);
      });
      personalizationOptions = [];
    }

    // Hide "Personalize It"
    personalizeIt.forEach(function(opt) {
      opt.style.display = 'none';
    });

    // Always-visible: ensure shown + add styling class + move above toggle
    var toggle = document.querySelector('.personalize-toggle');
    var productForm = getProductForm();
    // NOTE: Shopify product forms contain <input name="id"> for the variant
    // selector. HTML's named-element lookup makes `productForm.id` return
    // that input element, not the form's id attribute. Use getAttribute('id')
    // to get the string id reliably. See v2026-05-11 changelog.
    var formId = productForm ? productForm.getAttribute('id') : null;

    // -----------------------------------------------------------------------
    // FORM CONTAINMENT FIX
    // -----------------------------------------------------------------------
    if (toggle && productForm && !productForm.contains(toggle)) {
      console.log('[Milano] Toggle is outside product form — relocating inside');
      container.parentNode.insertBefore(toggle, container);
    }

    // For Shop This Look products (no toggle), wrap all always-visible options
    var stlWrapper = null;
    if (document.body.classList.contains('milano-shop-this-look') && alwaysVisible.length > 0) {
      stlWrapper = document.createElement('div');
      stlWrapper.className = 'milano-stl-options';
      container.parentNode.insertBefore(stlWrapper, container);
    }

    alwaysVisible.forEach(function(opt) {
      opt.style.display = '';
      opt.classList.add('milano-always-visible');

      // Add "What's This?" help link to the title
      var titleEl = opt.querySelector('.bold_option_title');
      if (titleEl && !titleEl.querySelector('.milano-help-link') && !titleEl.querySelector('.bold-help-link')) {
        var optName = getOptionName(opt);
        var helpId = findHelpPopupId(optName);
        if (helpId) {
          titleEl.appendChild(createHelpLink(helpId));
        }
      }

      if (stlWrapper) {
        stlWrapper.appendChild(opt);
      } else if (toggle && toggle.parentNode) {
        toggle.parentNode.insertBefore(opt, toggle);
      } else {
        container.parentNode.insertBefore(opt, container);
      }
    });

    if (personalizationOptions.length === 0) {
      if (formId) {
        alwaysVisible.forEach(function(opt) {
          stampFormAttribute(opt, formId);
        });
      }
      state.building = false;
      state.panelBuilt = true;
      console.log('[Milano] No personalization options — all options always-visible');
      return;
    }

    // Build panel
    var panel = document.createElement('div');
    panel.className = 'milano-panel';

    var content = document.createElement('div');
    content.className = 'milano-panel__content';
    content.style.display = 'none';
    content.style.overflow = 'hidden';

    personalizationOptions.forEach(function(item, idx) {
      var displayName = item.name || 'Option ' + (idx + 1);
      var rowData = createOptionRow(item.el, displayName);
      state.rows.push(rowData);
      content.appendChild(rowData.row);
      watchOptionVisibility(rowData);
      console.log('[Milano]   Row: "' + rowData.name + '"' +
        (rowData.isVisible ? '' : ' (hidden)'));
    });

    // Summary
    var summary = document.createElement('div');
    summary.className = 'milano-summary';
    state.summaryEl = summary;
    content.appendChild(summary);

    panel.appendChild(content);
    state.panelEl = panel;
    state.contentEl = content;

    if (toggle && toggle.parentNode) {
      toggle.parentNode.insertBefore(panel, toggle.nextSibling);
    } else if (container.nextSibling) {
      container.parentNode.insertBefore(panel, container.nextSibling);
    } else {
      container.parentNode.appendChild(panel);
    }

    if (formId) {
      stampFormAttribute(panel, formId);
      alwaysVisible.forEach(function(opt) {
        stampFormAttribute(opt, formId);
      });
      console.log('[Milano] Stamped form="' + formId + '" on relocated inputs');
    }

    if (toggle) {
      toggle.classList.add('personalize-toggle--ready');
    }

    var qtyWrapper = document.querySelector('.selector-wrapper--qty');
    var submitHolder = document.querySelector('.product__submit__holder');
    if (qtyWrapper && submitHolder) {
      submitHolder.parentNode.insertBefore(qtyWrapper, submitHolder);
      console.log('[Milano] Quantity selector repositioned');
    }

    state.panelBuilt = true;
    state.building = false;
    console.log('[Milano] Panel built: ' + state.rows.length + ' rows');

    if (getPersonalizeState()) {
      state.contentEl.style.display = '';
      state.contentEl.style.maxHeight = 'none';
      state.contentEl.style.opacity = '1';
      document.body.classList.add('personalize-yes');
    }

    updateSummary();

    addVariantHelpLinks();

    var variantArea = document.querySelector('.product__selectors, .product__form .product__block');
    if (variantArea) {
      var helpLinkObserver = new MutationObserver(function() {
        addVariantHelpLinks();
      });
      helpLinkObserver.observe(variantArea, { childList: true, subtree: true });
      state.observers.push(helpLinkObserver);
    }

    var lastVariantHref = location.href;
    var variantUrlCheck = setInterval(function() {
      if (location.href !== lastVariantHref) {
        lastVariantHref = location.href;
        setTimeout(addVariantHelpLinks, 100);
      }
    }, 500);
    state._stopVariantUrlCheck = function() { clearInterval(variantUrlCheck); };

    if (window.location.search.indexOf('customize=true') !== -1) {
      var checkbox = document.querySelector('[data-personalize-input][type="checkbox"]');
      if (checkbox && !checkbox.checked) {
        checkbox.checked = true;
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        console.log('[Milano] Auto-opened customization via URL param');
      }
    }
  }

  // =========================================================================
  // VARIANT HELP LINKS
  // =========================================================================

  function addVariantHelpLinks() {
    var labels = document.querySelectorAll(
      '.radio__legend__label.label-typography, .select__label.label-typography'
    );
    for (var i = 0; i < labels.length; i++) {
      var text = labels[i].textContent.trim();
      var helpId = findHelpPopupId(text);
      if (helpId && !labels[i].querySelector('.milano-help-link')) {
        labels[i].appendChild(createHelpLink(helpId));
        console.log('[Milano] Help link added to variant: "' + text + '" → #' + helpId);
      }
    }
  }

  // =========================================================================
  // INITIALIZATION
  // =========================================================================

  function waitForBoldOptions(callback) {
    var attempts = 0;
    var maxAttempts = 40;

    function check() {
      attempts++;
      var container = findOptionsContainer();
      if (container) {
        var count = container.querySelectorAll('.bold_option').length;
        console.log('[Milano] Found container (' + count + ' options, attempt ' + attempts + ')');
        state.container = container;
        callback();
        return;
      }
      if (attempts < maxAttempts) {
        setTimeout(check, 500);
      } else {
        console.log('[Milano] Gave up after ' + attempts + ' attempts');
        callback();
      }
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        setTimeout(check, 300);
      });
    } else {
      setTimeout(check, 300);
    }
  }

  // =========================================================================
  // EVENT HANDLERS
  // =========================================================================

  document.addEventListener('change', function(e) {
    var target = e.target;
    if (!target) return;
    try {
      if (target.matches('[data-personalize-input]') ||
          target.matches('select[name="properties[Personalize It]"]') ||
          target.matches('input[name="properties[Personalize It]"]')) {
        updatePersonalizeState();
      }
    } catch (ex) {}
  });

  document.addEventListener('shopify:section:load', function() {
    state.observers.forEach(function(o) { o.disconnect(); });
    state.observers = [];
    if (state._stopPriceSync) state._stopPriceSync();
    if (state._stopVariantUrlCheck) state._stopVariantUrlCheck();
    _syncStarted = false;
    state.panelBuilt = false;
    state.building = false;
    state.container = null;
    state.rows = [];
    state.currentlyOpen = null;

    var existing = document.querySelector('.milano-panel');
    if (existing) existing.remove();

    setTimeout(function() {
      state.container = findOptionsContainer();
      buildPanel();
    }, 500);
  });

  // =========================================================================
  // ATC BUTTON + CART BAR PRICE SYNC
  // =========================================================================

  // =========================================================================
  // DN DISCOUNT DETECTION — DOM parsing with DN-specific selectors
  // =========================================================================

  function syncATCPrice() {

    // ===================================================================
    // DN DISCOUNT — DOM parsing with DN-specific selectors (READ-ONLY)
    //
    // getDNDiscountCents() reads DN's rendered elements. Never writes to DN.
    // v2026-05-07a: Removed all DN-write functions (updateDNDisplay,
    // updateDNDataAttrs, getDNOriginalPriceCents, getDNDiscountRatio,
    // resetDNVariantBase, findDNStrikeTextEl, weAreWritingDN) to fix
    // cart drawer price corruption caused by PDP write-war loop.
    // ===================================================================

    var _dnCache = { cents: null, ts: 0 };

    function getDNDiscountCents() {
      var now = Date.now();
      if (_dnCache.cents !== null && now - _dnCache.ts < 800) return _dnCache.cents;

      var discount = 0;

      var strikeEl = document.querySelector(
        '.limoniapps-discountninja-strikethrough'
      );
      var saleEl = document.querySelector(
        '.limoniapps-discountninja-shortcode-discounted_price'
      );

      if (strikeEl && saleEl) {
        var origAttr = strikeEl.querySelector('[data-limoniapps-discountninja-price-cents-basecurrency]');
        var saleAttr = saleEl.querySelector('[data-limoniapps-discountninja-price-cents-basecurrency]');

        if (origAttr && saleAttr) {
          var oc = parseInt(origAttr.getAttribute('data-limoniapps-discountninja-price-cents-basecurrency'), 10) || 0;
          var sc = parseInt(saleAttr.getAttribute('data-limoniapps-discountninja-price-cents-basecurrency'), 10) || 0;
          if (oc > sc && sc > 0) discount = oc - sc;
        }

        if (discount === 0) {
          var origCents = parseCents(strikeEl.textContent);
          var saleCents = parseCents(saleEl.textContent);
          if (origCents > saleCents && saleCents > 0) discount = origCents - saleCents;
        }
      }

      _dnCache = { cents: discount, ts: now };
      return discount;
    }

    function invalidateDNCache() {
      _dnCache.cents = null;
      _dnCache.ts = 0;
    }

    // ===================================================================
    // STORE DN DISCOUNT RATIO — for cart drawer strikethrough
    //
    // DN discount codes apply at checkout, not in the cart. Cart JSON
    // shows original_price === final_price, so native Liquid can't render
    // strikethrough. We store the discount ratio here (PDP) and
    // fixDrawerPrices() reads it to render client-side strikethrough.
    // ===================================================================


    function storeDNRatio() {
      try {
        // DN is off on product pages (Theme Config → Product page → Off).
        // If no DN elements exist, leave sessionStorage alone — the ratio
        // was set from a collection page where DN is still active.
        var hasDNElements = !!document.querySelector(
          '.limoniapps-discountninja-strikethrough,' +
          '.limoniapps-discountninja-shortcode-discounted_price'
        );
        if (!hasDNElements) return;
 
        var discount = getDNDiscountCents();
        if (discount <= 0) {
          sessionStorage.removeItem('mw_dn_ratio');
          return;
        }
        var strikeEl = document.querySelector('.limoniapps-discountninja-strikethrough');
 
        if (!strikeEl) return;
        var origCents = 0;
        var attr = strikeEl.querySelector('[data-limoniapps-discountninja-price-cents-basecurrency]');
        if (attr) origCents = parseInt(attr.getAttribute('data-limoniapps-discountninja-price-cents-basecurrency'), 10) || 0;
        if (origCents === 0) origCents = parseCents(strikeEl.textContent);
        if (origCents > 0 && discount < origCents) {
          sessionStorage.setItem('mw_dn_ratio', (discount / origCents).toFixed(6));
        }
      } catch (e) { /* sessionStorage unavailable (incognito etc) */ }
    }

    // ===================================================================
    // PRICE ELEMENT FINDERS
    //
    // v2026-05-07a: Removed findDNStrikeTextEl() — was only used by
    // updateDNDisplay() which has been removed.
    // ===================================================================

    // SC Options managed price — variant + add-ons (may be hidden by DN)
    function findSCOptionsEl() {
      return document.querySelector('[data-product-price].bold_option_price_display');
    }

    // DN's visible sale price text — uses DN-specific classes only (safe, no false positives)
    function findDNSaleTextEl() {
      return document.querySelector(
        '.limoniapps-discountninja-shortcode-discounted_price .limoniapps-discountninja-money > .money,' +
        '.limoniapps-discountninja-shortcode-discounted_price .limoniapps-discountninja-money'
      );
    }

    // ATC price elements removed — ATC price hidden via CSS (v2026-05-07a).
    // DN and Palo Alto can write to ATC freely; it's hidden, no conflict.

    // Cart bar price
    function findCartBarPriceEl() {
      return document.querySelector('.cart-bar [data-product-price]');
    }

    // ===================================================================
    // PRICE PARSING & FORMATTING
    // ===================================================================

    function parseCents(text) {
      if (!text) return 0;
      var cleaned = (text + '').replace(/[^\d.]/g, '');
      return Math.round(parseFloat(cleaned) * 100) || 0;
    }

    function formatUSD(cents) {
      var d = (cents / 100).toFixed(2);
      var parts = d.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return '$' + parts[0] + '.' + parts[1];
    }

    // ===================================================================
    // COMPUTE FINAL PRICE
    //
    // v2026-05-07a: Uses stored DN ratio from sessionStorage when available
    // for accurate percentage discount on SC Options total (including
    // add-ons). Falls back to flat DN discount subtraction if no ratio
    // stored. The ratio is set by storeDNRatio() which reads DN's clean
    // DOM — no write-war risk.
    //
    // Priority:
    //  1. SC Options active + DN active → SC_total × (1 − ratio)
    //     Falls back to SC_total − flat_discount if no ratio stored
    //  2. SC Options active, no DN     → SC_total (unchanged)
    //  3. No SC Options, DN active     → DN sale price (already correct)
    //  4. Neither                       → Regular Palo Alto price
    //
    // Returns { finalCents, displayText, scTotalCents, dnDiscountCents }
    // or null if no price source found.
    // ===================================================================

    function computeFinalPrice() {
      var dnDiscount = getDNDiscountCents();
      var scEl = findSCOptionsEl();

      if (scEl) {
        var scCents = parseCents(scEl.textContent);
        if (scCents > 0) {
          var appliedDiscount = dnDiscount; // flat fallback
          // Use stored ratio for accurate percentage discount on full total
          try {
            var stored = sessionStorage.getItem('mw_dn_ratio');
            var ratio = stored ? parseFloat(stored) : 0;
            if (ratio > 0 && ratio < 1) appliedDiscount = Math.round(scCents * ratio);
          } catch (e) { /* sessionStorage unavailable */ }

          var finalCents = Math.max(0, scCents - appliedDiscount);
          return {
            finalCents: finalCents,
            displayText: formatUSD(finalCents),
            scTotalCents: scCents,
            dnDiscountCents: appliedDiscount
          };
        }
      }

      // No SC Options — check DN sale price (already correct, just read it)
      if (dnDiscount > 0) {
        var dnSaleEl = findDNSaleTextEl();
        if (dnSaleEl) {
          var dnCents = parseCents(dnSaleEl.textContent);
          if (dnCents > 0) {
            return {
              finalCents: dnCents,
              displayText: formatUSD(dnCents),
              scTotalCents: 0,
              dnDiscountCents: dnDiscount
            };
          }
        }
      }

      // Fallback: regular Palo Alto price
      var regEl = document.querySelector(
        '[data-price-wrapper]:not([data-atc-button]) [data-product-price]'
      );
      if (regEl) {
        var regCents = parseCents(regEl.textContent);
        if (regCents > 0) {
          return {
            finalCents: regCents,
            displayText: formatUSD(regCents),
            scTotalCents: 0,
            dnDiscountCents: 0
          };
        }
      }

      return null;
    }

    // ===================================================================
    // SYNC FUNCTIONS
    //
    // v2026-05-07a: Removed updateDNDisplay() call from doSyncFast().
    // We no longer write to DN's DOM — DN owns its own price display.
    // We only write to ATC button and cart bar (plain text, always correct).
    // ===================================================================

    var syncRunning = true;
    var lastSyncedBar = '';
    var lastSyncedPdp = '';
    var lastSyncedSplitit = '';
    var splititCachedHTML = '';
    var lastKlarnaSourcePrice = '';
    var dnCooldownUntil = 0; // pause sync while DN re-renders

    // --- Fast sync: cart bar only ---
    // v2026-05-07a: ATC button price hidden via CSS — no ATC writes needed.
    // DN owns the product price display area (strikethrough formatting).
    // We own the cart bar (clean number, correct final price with add-ons).
    function doSyncFast() {
      try {
        if (Date.now() < dnCooldownUntil) return;

        var p = computeFinalPrice();
        if (!p) return;

                var bar = findCartBarPriceEl();
        if (bar && bar.textContent !== p.displayText) {
          bar.textContent = p.displayText;
          if (p.displayText !== lastSyncedBar) {
            console.log('[Milano] Cart bar synced → ' + p.displayText);
            lastSyncedBar = p.displayText;
          }
        }
 
        syncProductPrice();
      } catch (e) { /* elements detached — next tick retries */ }
    }


    // --- Product price strikethrough (DN off on PDP, v2026-05-07b) ---
    //
    // DN is disabled on product pages to prevent cart drawer write-wars.
    // SC Options writes the raw undiscounted price to [data-product-price].
    // We read mw_dn_ratio from sessionStorage (set on collection pages
    // where DN is still active) and render strikethrough in a sibling
    // .mw-pdp-strike element.
    //
    // Safety:
    //  - Never writes to [data-product-price] (avoids MutationObserver loop)
    //  - Hides original via classList (not observed — no attributes:true)
    //  - Cache key (rawCents + ratio) prevents redundant DOM writes
    //  - order:-1 in CSS positions sale price before wizard's (+$X services)
 
    function syncProductPrice() {
      // If DN Dynamic Pricing is rendering on this page, let it handle
      // the product price display. We handle cart bar, review card, drawer,
      // services context, and BNPL — DN owns the PDP price strikethrough.
      var dnActive = !!document.querySelector(
        '.limoniapps-discountninja-strikethrough,' +
        '.limoniapps-discountninja-discountedprice,' +
        '[class*="limoniapps-discountninja-productprice"]'
      );
      if (dnActive) return;

      // Fallback: render our own strikethrough when DN is NOT on this page
      // (DN off on PDP, no sale active, or product not in promotion)
      var dnRatio = 0;
      try {
        var stored = sessionStorage.getItem('mw_dn_ratio');
        if (stored) dnRatio = parseFloat(stored) || 0;
      } catch (e) {}

      var priceWrapper = document.querySelector(
        '[data-price-wrapper]:not([data-atc-button])'
      );
      if (!priceWrapper) return;

      var priceEl = priceWrapper.querySelector('[data-product-price]');
      if (!priceEl) return;

      var rawCents = parseCents(priceEl.textContent);
      if (rawCents <= 0) return;

      var cacheKey = rawCents + ':' + dnRatio.toFixed(6);
      if (cacheKey === lastSyncedPdp) return;
      lastSyncedPdp = cacheKey;

      var saleEl = priceWrapper.querySelector('.mw-pdp-strike');

      if (dnRatio > 0 && dnRatio < 1) {
        var discountedCents = Math.round(rawCents * (1 - dnRatio));

        if (!saleEl) {
          saleEl = document.createElement('span');
          saleEl.className = 'mw-pdp-strike';
          priceEl.insertAdjacentElement('afterend', saleEl);
        }

        var newHTML =
          '<ins class="mw-pdp-sale">' + formatUSD(discountedCents) + '</ins>' +
          ' <del class="mw-pdp-orig">' + formatUSD(rawCents) + '</del>';
        if (saleEl.innerHTML !== newHTML) saleEl.innerHTML = newHTML;
        saleEl.style.display = '';

        if (!priceEl.classList.contains('mw-dn-hidden')) {
          priceEl.classList.add('mw-dn-hidden');
        }
      } else {
        priceEl.classList.remove('mw-dn-hidden');
        if (saleEl) saleEl.style.display = 'none';
      }
    }
 

    // --- PDP Sale Banner (reads mw_dn_ratio, v2026-05-08) ---
    //
    // Injects a tasteful promotional banner below the product price.
    // Self-removing when ratio clears. Promotional copy is in the HTML
    // string — change that line for future sales, or remove the CSS
    // block (.mw-sale-banner) to deactivate entirely.
    //
    // Hidden in wizard mode (wizard owns its own pricing display).

    function injectSaleBanner() {
      if (document.getElementById('mw-sale-banner')) return;

      var dnRatio = 0;
      try {
        var stored = sessionStorage.getItem('mw_dn_ratio');
        if (stored) dnRatio = parseFloat(stored) || 0;
      } catch (e) {}
      if (!(dnRatio > 0 && dnRatio < 1)) return;

      // Product eligibility check: only show banner if DN rendered on this product
      var dnRendered = !!document.querySelector(
        '.limoniapps-discountninja-strikethrough,' +
        '.limoniapps-discountninja-shortcode-discounted_price'
      );
      if (!dnRendered) return;

      var pctOff = Math.round(dnRatio * 100);

      var priceWrapper = document.querySelector(
        '[data-price-wrapper]:not([data-atc-button])'
      );
      if (!priceWrapper) return;

      var banner = document.createElement('div');
      banner.id = 'mw-sale-banner';
      banner.className = 'mw-sale-banner';
      banner.innerHTML =
        '<div class="mw-sale-banner__inner">' +
          '<span class="mw-sale-banner__badge">' + pctOff + '% off</span>' +
          '<span class="mw-sale-banner__copy">' +
            'Mother\u2019s Day Sale \u2014 applied at checkout' +
          '</span>' +
        '</div>';

      priceWrapper.insertAdjacentElement('afterend', banner);
      console.log('[Milano] PDP sale banner injected (' + pctOff + '% off)');
    }


    // --- Slow sync: BNPL (Splitit + Klarna) — only on price change ---
    function doSyncBNPL() {
      try {
        var p = computeFinalPrice();
        if (!p || p.finalCents <= 0) return;

        var amount = p.finalCents / 100; // dollars

        // ----- Splitit -----
        var splititContainer = document.getElementById('splitit-container');
        var splititIsEmpty = splititContainer && splititContainer.innerHTML.length < 10;

        if (splititContainer && splititContainer.innerHTML.length > 10 && !splititCachedHTML) {
          splititCachedHTML = splititContainer.innerHTML;
          console.log('[Milano] Splitit HTML cached (' + splititCachedHTML.length + ' chars)');
        }

        if (splititCachedHTML && (!splititContainer || splititIsEmpty)) {
          if (!splititContainer) {
            var priceWrapper = document.querySelector('[data-price-wrapper]:not([data-atc-button])');
            if (priceWrapper) {
              var sw = document.createElement('div');
              sw.className = 'splitit-wrapper';
              var sc = document.createElement('div');
              sc.id = 'splitit-container';
              sc.setAttribute('data-splitit', 'true');
              sc.setAttribute('data-splitit-num-installments', '3');
              sc.setAttribute('data-splitit-type', 'product-description');
              sw.appendChild(sc);
              priceWrapper.appendChild(sw);
              splititContainer = sc;
            }
          }
          if (splititContainer) {
            splititContainer.setAttribute('data-splitit-amount', amount.toFixed(2));
            splititContainer.innerHTML = splititCachedHTML;
            var numI = parseInt(splititContainer.getAttribute('data-splitit-num-installments') || '3', 10);
            var perM = (amount / numI).toFixed(2);
            var ps = splititContainer.querySelector('.-splitit---text-price')
                  || splititContainer.querySelector('.-splitit--text-price')
                  || splititContainer.querySelector('.splitit--text-price');
            if (ps) ps.textContent = '$' + perM;
            try {
              if (window.splitit && window.splitit.ui && typeof window.splitit.ui.refresh === 'function') {
                window.splitit.ui.refresh();
              }
            } catch (e2) { /* ignore */ }
            console.log('[Milano] Splitit restored: $' + amount.toFixed(2) + ' ($' + perM + '/mo)');
            lastSyncedSplitit = p.displayText;
          }
        }

        if (splititContainer && !splititIsEmpty) {
          var oldAmount = parseFloat(splititContainer.getAttribute('data-splitit-amount') || '0');
          if (Math.abs(amount - oldAmount) > 0.01) {
            splititContainer.setAttribute('data-splitit-amount', amount.toFixed(2));
            try {
              if (window.splitit && window.splitit.ui && typeof window.splitit.ui.refresh === 'function') {
                window.splitit.ui.refresh();
              }
            } catch (e2) { /* ignore */ }
            var numInstallments = parseInt(splititContainer.getAttribute('data-splitit-num-installments') || '3', 10);
            var perMonth = (amount / numInstallments).toFixed(2);
            var priceSpan = splititContainer.querySelector('.-splitit---text-price')
                         || splititContainer.querySelector('.-splitit--text-price')
                         || splititContainer.querySelector('.splitit--text-price');
            if (priceSpan) priceSpan.textContent = '$' + perMonth;
            console.log('[Milano] Splitit synced: $' + oldAmount.toFixed(2) + ' → $' + amount.toFixed(2) + ' ($' + perMonth + '/mo)');
            lastSyncedSplitit = p.displayText;
          }
        }

        if (!splititContainer && !splititCachedHTML) {
          var fallback = document.querySelector('.milano-splitit-fallback');
          var perMo = (amount / 3).toFixed(2);
          var splititLogoUrl = 'https://splitit.com/wp-content/themes/starter/images/logo/full-green.svg';
          var existingLogo = document.querySelector('.splitit-wrapper img, .milano-qv-splitit img');
          if (existingLogo && existingLogo.src) splititLogoUrl = existingLogo.src;

          if (!fallback) {
            var priceWrapper = document.querySelector('[data-price-wrapper]:not([data-atc-button])');
            if (priceWrapper) {
              fallback = document.createElement('div');
              fallback.className = 'milano-splitit-fallback';
              fallback.style.cssText = 'display:flex;align-items:center;gap:4px;flex-wrap:wrap;font-size:13px;color:#888;line-height:1.4;margin:6px 0 12px;padding:0;';
              priceWrapper.appendChild(fallback);
            }
          }

          if (fallback) {
            var newHtml = 'From <strong style="color:#333">$' + perMo + '</strong>/month with ' +
              '<img src="' + splititLogoUrl + '" alt="Splitit" style="height:18px;vertical-align:middle;" onerror="this.outerHTML=\'<strong style=color:#2fad4a>Splitit</strong>\'">' +
              ' <a href="https://www.splitit.com/" target="_blank" rel="noopener" style="color:#888;text-decoration:underline;margin-left:2px;font-size:13px;">Learn more</a>';
            if (p.displayText !== lastSyncedSplitit) {
              fallback.innerHTML = newHtml;
              console.log('[Milano] Splitit fallback: $' + amount.toFixed(2) + ' ($' + perMo + '/mo)');
              lastSyncedSplitit = p.displayText;
            }
          }
        }

        // ----- Klarna -----
        if (p.displayText !== lastKlarnaSourcePrice) {
          lastKlarnaSourcePrice = p.displayText;
          var amountCents = Math.round(amount * 100);

          var setKlarnaAmount = function() {
            var el = document.querySelector('klarna-placement[data-purchase-amount]');
            if (el) el.setAttribute('data-purchase-amount', amountCents.toString());
          };

          setKlarnaAmount();
          setTimeout(setKlarnaAmount, 500);
          setTimeout(setKlarnaAmount, 1200);

          console.log('[Milano] Klarna synced to ' + amountCents + ' cents ($' + amount.toFixed(2) + ')');
        }
      } catch (e) { /* elements detached — next tick retries */ }
    }

    // ===================================================================
    // RAF LOOP — Throttled polling
    // ===================================================================

    var lastRun = 0;
    var lastFastRun = 0;
    var lastSplititCheck = 0;
    var lastRAFDisplay = '';

    function rafLoop() {
      if (!syncRunning) return;
      var now = Date.now();

      if (now - lastFastRun >= 200) {
        doSyncFast();
        lastFastRun = now;
      }

      if (now - lastRun >= 500) {
        var p = computeFinalPrice();
        var curDisplay = p ? p.displayText : '';
        if (curDisplay && curDisplay !== lastRAFDisplay) {
          lastRAFDisplay = curDisplay;
          doSyncBNPL();
        }
        lastRun = now;
      }

      if (now - lastSplititCheck >= 2000) {
        var sc = document.getElementById('splitit-container');
        if (splititCachedHTML && (!sc || sc.innerHTML.length < 10)) {
          doSyncBNPL();
        }
        lastSplititCheck = now;
      }

      requestAnimationFrame(rafLoop);
    }

    // ===================================================================
    // OBSERVERS — Watch SC Options + DN for changes
    //
    // v2026-05-07a: DN observer simplified — removed weAreWritingDN guard
    // (we no longer write to DN) and resetDNVariantBase() call (removed).
    // Observer now only invalidates cache and triggers cooldown.
    // ===================================================================

    function observeSources() {
      var bnplDebounce = null;
      function onPriceChange() {
        invalidateDNCache();
        storeDNRatio();
        doSyncFast();
        clearTimeout(bnplDebounce);
        bnplDebounce = setTimeout(doSyncBNPL, 150);
      }

      // Watch SC Options price element
      var scEl = findSCOptionsEl();
      if (scEl) {
        var obs1 = new MutationObserver(onPriceChange);
        obs1.observe(scEl, { childList: true, characterData: true, subtree: true });
        state.observers.push(obs1);
        if (scEl.parentElement) {
          var obs2 = new MutationObserver(onPriceChange);
          obs2.observe(scEl.parentElement, { childList: true, subtree: true });
          state.observers.push(obs2);
        }
      }

      // Watch DN container for re-renders (variant change triggers full replace)
      var dnContainer = document.querySelector('[data-la-dn-price] [data-price-wrapper]')
                     || document.querySelector('[data-la-dn-price]')
                     || document.querySelector('.limoniapps-discountninja-productprice-dynamic');
      if (dnContainer) {
        var dnDebounce = null;
        var obs3 = new MutationObserver(function() {
          // v2026-05-07a: No weAreWritingDN guard needed — we never write to DN
          invalidateDNCache();
          dnCooldownUntil = Date.now() + 400;
          clearTimeout(dnDebounce);
          dnDebounce = setTimeout(function() {
            dnCooldownUntil = 0;
            invalidateDNCache();
            onPriceChange();
          }, 400);
        });
        obs3.observe(dnContainer, { childList: true, subtree: true });
        state.observers.push(obs3);
      }

      // Watch the data-price-wrapper in case Palo Alto replaces the whole block
      var priceWrapper = document.querySelector('[data-price-wrapper]:not([data-atc-button])');
      if (priceWrapper && priceWrapper !== (scEl && scEl.parentElement)) {
        var obs4 = new MutationObserver(onPriceChange);
        obs4.observe(priceWrapper, { childList: true, subtree: true });
        state.observers.push(obs4);
      }
    }

    // ===================================================================
    // START
    // ===================================================================

    if (_syncStarted) return;
    _syncStarted = true;
    console.log('[Milano] Price sync starting (DN-readonly v2026-05-07b)');

    // Hide ATC button price — price shown on product form, cart bar, and
    // wizard review card. Eliminates the DN vs SC Options price conflict on
    // the ATC button entirely. Scoped to PDP only (syncATCPrice only runs on PDP).
    if (!document.getElementById('mw-atc-price-hide')) {
      var hideStyle = document.createElement('style');
      hideStyle.id = 'mw-atc-price-hide';
      hideStyle.textContent =
        '[data-price-wrapper][data-atc-button] [data-product-price],' +
        'button[name="add"] [data-product-price],' +
        '.bold_clone[type="submit"] [data-product-price] {' +
          'display:none !important' +
        '}' +
        '[data-product-price].mw-dn-hidden{display:none !important}' +
        '.mw-pdp-strike{display:inline;font-size:inherit;line-height:inherit;order:-1}' +
        '.mw-pdp-strike ins.mw-pdp-sale{color:#C45349;text-decoration:none;font-weight:inherit}' +
        '.mw-pdp-strike del.mw-pdp-orig{color:#9A8E84;text-decoration:line-through;margin-left:8px;font-weight:inherit}' +
        '.bold_option_total{display:none!important}';
 
      document.head.appendChild(hideStyle);
    }

    observeSources();
    doSyncBNPL();
    requestAnimationFrame(rafLoop);

    // Store DN ratio after DN has had time to render (for cart drawer strikethrough)
    setTimeout(storeDNRatio, 2000);

    // Inject PDP sale banner after ratio is stored
    setTimeout(injectSaleBanner, 2200);

    state._stopPriceSync = function() { syncRunning = false; };
  }

  // =========================================================================
  // CART DRAWER — STRIKETHROUGH FIX (v2026-05-07b)
  //
  // Palo Alto wraps the drawer in <template> and rebuilds ALL cart item
  // HTML on every cart operation (add, quantity change, remove, reopen).
  // v2026-05-07b: Added theme:cart:change + drawer-open observer so
  // fixDrawerPrices() re-runs on all cart operations, not just adds.
  // =========================================================================

  (function() {
    var _fixing = false;

    function fmtMoney(cents) {
      var d = (cents / 100).toFixed(2);
      var parts = d.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return '$' + parts[0] + '.' + parts[1];
    }

    function fixDrawerPrices() {
      if (_fixing) return;
      _fixing = true;

      var dnRatio = 0;
      try {
        var stored = sessionStorage.getItem('mw_dn_ratio');
        if (stored) dnRatio = parseFloat(stored) || 0;
      } catch (e) {}

      fetch('/cart.json', { credentials: 'same-origin' })
        .then(function(r) { return r.json(); })
        .then(function(cart) {
          if (!cart || !cart.items || cart.items.length === 0) return;

          var map = {};
          for (var i = 0; i < cart.items.length; i++) {
            map[cart.items[i].key] = cart.items[i];
          }

          var rows = document.querySelectorAll('.cart__item[data-item]');
          var fixed = 0;

          // Check if Shopify Functions is handling discounts server-side.
          // If ANY item has original_price > final_price, the server knows
          // which products are eligible. Case 2 (ratio-based) is only needed
          // when DN uses checkout-only codes without Shopify Functions.
          var hasServerDiscount = false;
          for (var k = 0; k < cart.items.length; k++) {
            if (cart.items[k].original_price > cart.items[k].final_price) {
              hasServerDiscount = true;
              break;
            }
          }

          for (var j = 0; j < rows.length; j++) {
            var row = rows[j];
            var key = row.getAttribute('data-item');
            var item = map[key];
            if (!item) continue;

            var priceEl = row.querySelector('.cart__item__price');
            if (!priceEl) continue;
            if (priceEl.querySelector('ins')) continue;

            // Case 1: Server-side discount (Shopify Functions)
            if (item.original_price > item.final_price) {
              priceEl.innerHTML = '<ins>' + fmtMoney(item.final_price) + '</ins><del>' + fmtMoney(item.original_price) + '</del>';
              fixed++;
              continue;
            }

            // Case 2: DN discount code (applies at checkout, not in cart)
            // Skip when Shopify Functions is active — it handles eligible items
            // via Case 1, and ineligible items (accessories) should not get
            // ratio-based strikethrough.
            if (!hasServerDiscount && dnRatio > 0 && item.final_price > 0) {
              var discountedCents = Math.round(item.final_price * (1 - dnRatio));
              if (discountedCents < item.final_price && discountedCents > 0) {
                priceEl.innerHTML = '<ins>' + fmtMoney(discountedCents) + '</ins><del>' + fmtMoney(item.final_price) + '</del>';
                fixed++;
              }
            }
          }

          if (fixed > 0) {
            console.log('[Milano] Drawer strikethrough fixed: ' + fixed + ' item(s)' +
              (dnRatio > 0 ? ' (DN ratio: ' + (dnRatio * 100).toFixed(0) + '%)' : ''));
          }
        })
        .catch(function(e) {
          console.warn('[Milano] Drawer price fix failed:', e);
        })
        .then(function() {
          _fixing = false;
        });
    }

    // Retry helper — Palo Alto renders async, may need multiple attempts
    function runDrawerFix() {
      var attempts = 0;
      function tryFix() {
        if (attempts >= 5) return;
        attempts++;
        fixDrawerPrices();
        setTimeout(tryFix, 1500);
      }
      setTimeout(tryFix, 800);
    }

    // 1. New item added to cart
    document.addEventListener('theme:cart:added', runDrawerFix);

    // 2. Quantity change or item removal — Palo Alto rebuilds cart HTML
    document.addEventListener('theme:cart:change', runDrawerFix);

    // 3. Cart drawer open — body gets js-drawer-open-cart class
    //    Catches reopening drawer without adding/changing items
    var _lastDrawerState = false;
    var _drawerObs = new MutationObserver(function() {
      var isOpen = document.body.classList.contains('js-drawer-open-cart');
      if (isOpen && !_lastDrawerState) {
        setTimeout(fixDrawerPrices, 300);
        setTimeout(fixDrawerPrices, 1200);
      }
      _lastDrawerState = isOpen;
    });
    _drawerObs.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    console.log('[Milano] Drawer strikethrough listeners registered (added + change + drawer-open)');
  })();


  // =========================================================================
  // START
  // =========================================================================

  if (isPDP) {
    setTimeout(syncATCPrice, 3500);

    waitForBoldOptions(function() {
      buildPanel();
      setTimeout(function() {
        if (!state.panelBuilt) buildPanel();
        syncATCPrice();
      }, 2000);
    });

    // =====================================================================
    // ATC SAFETY NET — Bold Clone fallback
    // =====================================================================
    (function() {
      var safetyTimer = null;
      var safetyActive = false;

      function clearSafety() {
        if (safetyTimer) {
          clearTimeout(safetyTimer);
          safetyTimer = null;
        }
        safetyActive = false;
      }

      document.addEventListener('theme:cart:added', clearSafety);

      document.addEventListener('click', function(e) {
        var btn = e.target.closest('button.bold_clone[type="submit"]');
        if (!btn || safetyActive) return;

        if (btn.classList.contains('is-loading') || btn.classList.contains('is-success')) return;

        safetyActive = true;
        safetyTimer = setTimeout(function() {
          if (document.body.classList.contains('js-drawer-open-cart')) {
            clearSafety();
            return;
          }

          if (btn.classList.contains('is-loading')) {
            clearSafety();
            return;
          }

          var form = btn.closest('form[action*="/cart/add"]');
          if (form) {
            console.warn('[Milano] ATC safety net activated — forcing form submit');
            var productForm = btn.closest('product-form');
            if (productForm && typeof productForm.onSubmitHandler === 'function') {
              var fakeEvent = { preventDefault: function() {} };
              try {
                productForm.onSubmitHandler(fakeEvent);
              } catch (err) {
                console.warn('[Milano] Safety net product-form submit failed, using native:', err);
                form.submit();
              }
            } else {
              form.submit();
            }
          }
          clearSafety();
        }, 3000);
      }, true);
    })();
  }

})();